import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { AnimatePresence } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { NovaAccessGate } from "~/components/nova/nova-access-gate";
import { NovaCreationOverlay } from "~/components/nova/nova-creation-overlay";
import { isNovaTabPage, NovaTopNav } from "~/components/nova/nova-top-nav";
import { NovaWorkspaceSync } from "~/components/nova/nova-workspace-sync";
import { isNovaWalled, useNovaAccess } from "~/components/nova/use-nova-access";
import {
  predictWillContinue,
  useActiveNovaConversationStart,
  useStartNovaConversation,
} from "~/components/nova/use-start-nova-conversation";
import { useWakeNovaRoutines } from "~/components/nova/use-wake-nova-routines";
import { UpgradePromptSlot } from "~/components/upgrade-prompts/upgrade-prompt-slot";
import { getNovaConversations } from "~/lib/functions/nova";
import { recordMyNovaOpen } from "~/lib/functions/nova-opens";
import { isFreeNovaAgent } from "~/lib/shared/nova/agents";
import { novaAgentTypeSchema } from "~/lib/shared/nova/schemas";
import {
  UPGRADE_OFFER_FOR_LINK,
  UPGRADE_OFFER_LINKS,
  type UpgradeOfferKind,
} from "~/lib/shared/upgrade-prompts";

export const Route = createFileRoute("/_user/my-coaching/business/nova")({
  // `start` lets other pages and old links deep-link straight into a fresh
  // session with a given agent. `assets` opens My content instead: every
  // asset starts from its own card there or in My assets. An unknown agent (an old bookmark naming a retired agent) parses to
  // undefined so the link still opens Nova instead of crashing the route.
  // A return from Stripe is re-synced by the /my-coaching layout before this
  // mounts (`NovaCheckoutReturn`).
  // `offer` is the upgrade emails' link (`?offer=accelerator`, `?offer=yearly`):
  // it opens that offer's pop-up on a NOVA tab (see `UpgradePromptSlot`). An
  // unknown value opens NOVA as if it were not there.
  validateSearch: z.object({
    start: novaAgentTypeSchema.optional().catch(undefined),
    offer: z.enum(UPGRADE_OFFER_LINKS).optional().catch(undefined),
  }),
  staticData: { title: "NOVA" },
  // Opening any NOVA page records her NOVA day, once per UTC day: the date in
  // the key and the endless cache skip the call after the first page of the
  // day. A preload is not a visit. `prefetchQuery` never throws, so a failed
  // write leaves the page working (the server reports it to Sentry); throwing
  // on it keeps the failure out of the cache, so her next NOVA page tries
  // again, and no retries hold the page.
  loader: async ({ context, preload }) => {
    if (preload) return;
    await context.queryClient.prefetchQuery({
      queryKey: ["nova", "opened", new Date().toISOString().slice(0, 10)],
      queryFn: async () => {
        const result = await recordMyNovaOpen();
        if (result === "failed") throw new Error("NOVA open not recorded");
        return result;
      },
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
    });
  },
  component: NovaLayout,
});

function NovaLayout() {
  const startConversation = useStartNovaConversation();
  const activeStart = useActiveNovaConversationStart();
  const navigate = Route.useNavigate();
  const { start, offer } = Route.useSearch();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: subscription } = useNovaAccess();
  const { user } = Route.useRouteContext();
  useWakeNovaRoutines();

  // The link's offer is held here, for the page it opened, and dropped from
  // the address at once, so a refresh, the back button or another NOVA page
  // follows the normal rules.
  const [requested, setRequested] = useState<{
    offer: UpgradeOfferKind;
    pathname: string;
  } | null>(offer ? { offer: UPGRADE_OFFER_FOR_LINK[offer], pathname } : null);
  useEffect(() => {
    if (!offer) return;
    setRequested({ offer: UPGRADE_OFFER_FOR_LINK[offer], pathname });
    void navigate({
      search: (prev) => ({ ...prev, offer: undefined }),
      replace: true,
    });
  }, [offer, pathname, navigate]);
  useEffect(() => {
    if (requested && requested.pathname !== pathname) setRequested(null);
  }, [requested, pathname]);

  const { data: conversations } = useQuery({
    queryKey: ["nova", "conversations"],
    queryFn: () => getNovaConversations({ data: {} }),
  });

  // Deep-link auto-start: when navigated here with `?start=<agent>`, kick off a
  // fresh conversation exactly once, then strip the param so a refresh or back
  // navigation doesn't spawn a duplicate.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (start === "assets") {
      autoStarted.current = true;
      void navigate({
        to: "/my-coaching/business/nova/content",
        search: {},
        replace: true,
      });
      return;
    }
    // Wait for `conversations` to load so `predictWillContinue` sees real data
    // (matters for Diagnose).
    if (!start || !conversations || autoStarted.current) return;
    // Skip auto-start for a walled agent with no access — createNovaConversation
    // would reject it server-side. Strip the param so the coach lands on NOVA
    // Home, which offers her way in, instead.
    const walledAndBlocked =
      !isFreeNovaAgent(start) && isNovaWalled(subscription);
    if (walledAndBlocked) {
      autoStarted.current = true;
      void navigate({
        to: "/my-coaching/business/nova",
        search: {},
        replace: true,
      });
      return;
    }
    autoStarted.current = true;
    void navigate({
      to: "/my-coaching/business/nova",
      search: {},
      replace: true,
    });
    if (!startConversation.isPending) {
      startConversation.mutate({
        agentType: start,
        willContinue: predictWillContinue(start, conversations),
      });
    }
  }, [start, navigate, startConversation, conversations, subscription]);

  return (
    // `relative` anchors NovaCreationOverlay's `absolute inset-0` to the
    // content area (the overlay no longer lives inside the workspace shell).
    // The overlay comes last, so the page rhythm never offsets it.
    <div className="relative space-y-8">
      <NovaTopNav />
      {isNovaTabPage(pathname) && (
        <UpgradePromptSlot
          onRequestDone={() => setRequested(null)}
          requestedOffer={
            requested?.pathname === pathname ? requested.offer : null
          }
          surface="nova_tab"
        />
      )}
      <NovaAccessGate userId={user.clerkUserId}>
        <NovaWorkspaceSync />
        <Outlet />
      </NovaAccessGate>
      <AnimatePresence>
        {activeStart && (
          <NovaCreationOverlay
            agentType={activeStart.agentType}
            willContinue={activeStart.willContinue}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
